import NextLink from 'next/link';
import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Link } from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { useStyles } from './styles';
import useIsMobile from 'hooks/useIsMobile';
import { useRouter } from 'next/router';
import { getEnv } from 'utils';
import Head from 'next/head';

const Props = {
  items: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      path: PropTypes.string,
    }),
  ).isRequired,
  showLinkHome: PropTypes.bool,
};

const DefaultProps = {
  showLinkHome: true,
};

export default function Breadcrumb({ items, showLinkHome }) {
  const classes = useStyles();
  const isMobile = useIsMobile();
  const { asPath, pathname } = useRouter();
  const slugPage = pathname === '/infographic-templates/[slug]';

  const pageFormat = asPath.includes('format');

  const titleBreadcrumb = items.filter((el) => !el.path);

  // Generate the breadcrumbs schema
  const generateBreadcrumbsSchema = () => {
    // Create a complete list of breadcrumbs for the schema
    const schemaBreadcrumbs = [];

    // Add Home breadcrumb if it's shown in the UI
    if (showLinkHome) {
      schemaBreadcrumbs.push({
        label: 'Home',
        path: '/',
      });
    }

    // Add all other breadcrumbs from items prop
    items.forEach((item) => {
      // Only include items with a path (clickable breadcrumbs)
      if (item.path || (schemaBreadcrumbs.length === 0 && !showLinkHome)) {
        schemaBreadcrumbs.push(item);
      }
    });

    // Create the schema object
    return {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: schemaBreadcrumbs.map((crumb, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        item: {
          '@id': `${getEnv('MTG_URL_APP') || 'https://mindthegraph.com'}${crumb.path || asPath}`,
          name: crumb.label,
        },
      })),
    };
  };

  const breadcrumbsSchema = generateBreadcrumbsSchema();

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbsSchema) }}
          key="breadcrumbs-jsonld" // Add a key to prevent duplicates
        />
      </Head>
      <Box display="flex" alignItems="center">
        {showLinkHome && (
          <>
            <NextLink href={`${getEnv('MTG_URL_APP')}/`} passHref={true}>
              <Link variant={isMobile ? 'body1' : 'h6'} className={classes.link} underline="hover">
                Home
              </Link>
            </NextLink>

            <ChevronRight className={classes.icon} />
          </>
        )}

        {items.map((item, index) => {
          const isLastItem = index === items.length - 1;
          const isNextItemLastWithPath =
            index < items.length - 1 && items.slice(index + 1).every((nextItem) => !nextItem.path);

          return (
            <Box key={index} display="flex" alignItems="center">
              <Typography
                variant={isMobile ? 'body1' : 'h6'}
                component={index === 2 ? 'h1' : pageFormat && index === 1 ? 'h1' : 'body1'}
              >
                <b>
                  {item.path && (
                    <NextLink
                      href={`${getEnv('MTG_URL_APP')}${item.path}`}
                      prefetch={false}
                      passHref={true}
                    >
                      <Link className={classes.link} underline="hover">
                        {item.label}
                      </Link>
                    </NextLink>
                  )}
                </b>
              </Typography>

              {!isNextItemLastWithPath && index !== items.length - 1 && (
                <ChevronRight className={classes.icon} />
              )}
            </Box>
          );
        })}
      </Box>

      {slugPage && (
        <Typography variant="body1" className={classes.titleBreadcrumb}>
          {titleBreadcrumb[0].label}
        </Typography>
      )}
    </>
  );
}

Breadcrumb.propTypes = Props;
Breadcrumb.defaultProps = DefaultProps;
